/**
 * useIngestionHydration
 *
 * Rehydrates form data from a previously validated ingestion on page refresh.
 *
 * When `?ingestion=<id>&validated=true` is present in the URL, this hook:
 * 1. Fetches the ingestion record and its jobs
 * 2. Parses validation results to extract file_format + original file URI
 * 3. Parses profiling results to extract DataProfile (columns, statistics)
 * 4. Returns everything needed to repopulate the ingest form
 *
 * If the backend data contradicts the validated=true URL state (e.g. validation
 * or profiling jobs failed, no jobs exist, or fetch errors), the hook removes
 * the validated param from the URL so the form doesn't incorrectly rehydrate.
 *
 * This hook intentionally does NOT write to form state — the caller (IngestForm)
 * is responsible for calling setValue() with the returned data.
 */
import { useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { DataProfile } from "datasets/pages/tables/components/file-validation/types";
import {
  parseValidationJobResults,
  parseProfilingJobResults,
  recoverSourceFileUri,
  stripS3Prefix,
} from "datasets/pages/tables/components/file-validation/utils/jobParsers";
import { FileFormat } from "datasets/pages/tables/types/interfaces/ingest";
import {
  DataIngestionResponse,
  JobResponse,
} from "datasets/pages/tables/types/interfaces/ingestion-job";
import { encodeObjectKey, type FilesListResponse, filesListOptions } from "files/hooks/useFiles";
import { useFetcher } from "~/hooks/useFetcher";
import { FILE_BROWSER_API } from "~/lib/fileBrowserApi";

const { VITE_CATALOG_API } = import.meta.env;

export interface RecoveredFormData {
  /** Original file key (platform_storage_key) */
  platformStorageKey: string | null;
  /** Original file URI (source_file_uri — the file the user uploaded) */
  sourceFileUri: string | null;
  /** File format derived from validation (skip_header: 1, detected delimiter, etc.) */
  fileFormat: FileFormat | null;
  /** Data profile from profiling job */
  dataProfile: DataProfile | null;
  /** File size in bytes, fetched from storage */
  fileSize: number | undefined;
}

export interface UseIngestionHydrationResult {
  /** True while fetching ingestion/jobs data */
  isHydrating: boolean;
  /** Hydrated form data (null if not ready or hydration not applicable) */
  hydratedData: RecoveredFormData | null;
}

/**
 * Checks whether the backend job data actually supports a "validated" state.
 * Returns true if validation passed (or warned) AND profiling produced a DataProfile.
 */
function isHydrationValid(
  jobs: JobResponse[] | undefined,
  dataProfile: DataProfile | null
): boolean {
  if (!jobs || jobs.length === 0) {
    return false;
  }

  const validationJob = jobs.find((j) => j.job_type === "validation");
  const profilingJob = jobs.find((j) => j.job_type === "profiling");

  // Validation job must exist and not have a FAILED status
  if (!validationJob || validationJob.status === "FAILED") {
    return false;
  }

  // Check if any individual validation task failed
  const validationResults = parseValidationJobResults(validationJob);
  if (validationResults.hasErrors) {
    return false;
  }

  // Profiling job must exist and not have failed
  if (!profilingJob || profilingJob.status === "FAILED") {
    return false;
  }

  // We need a usable DataProfile to populate the form
  if (!dataProfile) {
    return false;
  }

  return true;
}

export const useIngestionHydration = (): UseIngestionHydrationResult => {
  const { datasetId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasStrippedValidated = useRef(false);

  const ingestionId = searchParams.get("ingestion");
  const isValidated = searchParams.get("validated") === "true";

  // Only attempt hydration when both params are present
  const shouldHydrate = !!datasetId && !!ingestionId && isValidated;

  // Only activate on page refresh — i.e. when the component mounted with
  // shouldHydrate already true. When validated=true is added dynamically
  // (user clicks "Accept & Continue"), the form is already populated by
  // handleFileValidated and no hydration is needed.
  const mountedWithHydration = useRef(shouldHydrate);
  const shouldFetch = mountedWithHydration.current && shouldHydrate;

  // Fetch ingestion record
  const {
    data: ingestion,
    isLoading: isLoadingIngestion,
    error: ingestionError,
  } = useFetcher<DataIngestionResponse>(
    shouldFetch ? `/api/catalog/datasets/${datasetId}/ingestions/${ingestionId}` : null
  );

  // Fetch all jobs for this ingestion
  const {
    data: jobs,
    isLoading: isLoadingJobs,
    error: jobsError,
  } = useFetcher<JobResponse[]>(
    shouldFetch ? `${VITE_CATALOG_API}/datasets/${datasetId}/ingestions/${ingestionId}/jobs` : null
  );

  // Determine if the backend data actually supports hydration
  const isLoading = isLoadingIngestion || isLoadingJobs;
  const hasError = !!ingestionError || !!jobsError;
  const hasJobs = !!jobs && jobs.length > 0;

  // Parse results (only when data is available)
  let hydratedData: RecoveredFormData | null = null;
  let dataProfile: DataProfile | null = null;
  let recoveredFileKey: string | null = null;

  if (shouldFetch && !isLoading && !hasError && hasJobs) {
    const validationJob = jobs.find((j) => j.job_type === "validation");
    const profilingJob = jobs.find((j) => j.job_type === "profiling");

    const validationResults = validationJob
      ? parseValidationJobResults(validationJob)
      : { validatedFilePath: null, fileType: null, errorRows: 0 };

    dataProfile = profilingJob
      ? parseProfilingJobResults(profilingJob, validationResults.errorRows)
      : null;

    if (isHydrationValid(jobs, dataProfile)) {
      const fileType = validationResults.fileType || "csv";
      const delimiter = fileType === "tsv" ? "\t" : ",";
      const fileFormat: FileFormat = {
        type: "csv", // Snowflake only supports CSV/TSV, TSV is handled via delimiter
        delimiter,
        encoding: "UTF8",
        null_if: ["\\N", "NULL", ""],
        skip_header: 1, // original file has a header
        skip_error_rows: true, // use ON_ERROR = CONTINUE during ingest
      };

      const originalFileUri = recoverSourceFileUri(jobs, ingestion);
      const platformStorageKey = stripS3Prefix(originalFileUri);
      recoveredFileKey = platformStorageKey;

      hydratedData = {
        platformStorageKey,
        sourceFileUri: originalFileUri,
        fileFormat,
        dataProfile,
        fileSize: undefined,
      };
    }
  }

  // Fetch file metadata from the File Browser to recover file size on page refresh.
  // The file is already in S3 so the File Browser API can return its size.
  const { data: fileBrowse } = useFetcher<FilesListResponse>(
    recoveredFileKey ? `${FILE_BROWSER_API}/files/${encodeObjectKey(recoveredFileKey)}` : null,
    filesListOptions
  );
  if (hydratedData && fileBrowse?.current_file?.size) {
    hydratedData.fileSize = fileBrowse.current_file.size;
  }

  // Strip validated=true from the URL if the backend doesn't support it.
  // Only runs on page refresh (shouldFetch=true). When validated=true is set
  // dynamically, shouldFetch is false and this effect is a no-op.
  useEffect(() => {
    if (!shouldFetch || isLoading || hasStrippedValidated.current) {
      return;
    }

    const shouldStrip = hasError || !hasJobs || !hydratedData;
    if (shouldStrip) {
      hasStrippedValidated.current = true;
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("validated");
      setSearchParams(newParams, { replace: true });
    }
  }, [shouldFetch, isLoading, hasError, hasJobs, hydratedData, searchParams, setSearchParams]);

  // Not applicable — no hydration needed (either no URL params, or set dynamically)
  if (!shouldFetch) {
    return { isHydrating: false, hydratedData: null };
  }

  // Still loading
  if (isLoading) {
    return { isHydrating: true, hydratedData: null };
  }

  // Hydration succeeded
  if (hydratedData) {
    return { isHydrating: false, hydratedData };
  }

  // Hydration failed — validated param will be stripped by the effect above
  return { isHydrating: false, hydratedData: null };
};
